import React, { useState } from 'react';
import { SparklesIcon, MagnifyingGlassIcon, XMarkIcon, GlobeAltIcon } from './Icons';
import { searchMarketIntel } from '../services/geminiService';
import type { MarketIntelResponse } from '../types';

const MarketIntel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [result, setResult] = useState<MarketIntelResponse | null>(null);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    
    setIsSearching(true);
    try {
      const data = await searchMarketIntel(query);
      setResult(data);
    } catch (err) {
      console.error("Search failed:", err);
    } finally {
      setIsSearching(false);
    }
  };

  const closeIntel = () => {
    setIsOpen(false);
    setResult(null);
    setQuery('');
  };

  if (!isOpen) {
    return (
      <button 
        onClick={() => setIsOpen(true)}
        className="relative z-50 bg-btn-primary text-btn-primary-text p-4 rounded-full shadow-2xl hover:bg-btn-primary-hover transition-all transform hover:scale-110 active:scale-95 group flex items-center justify-center min-w-[56px] min-h-[56px]"
        title="Market Intelligence"
        aria-label="Market Intelligence"
      >
        <SparklesIcon className="w-6 h-6 animate-pulse" />
        <span className="absolute right-full mr-3 bg-brand-green text-white text-[10px] font-black uppercase px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block pointer-events-none">
          Market Intel AI
        </span>
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-md animate-in fade-in duration-300">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="market-intel-title"
        className="bg-card-bg rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 duration-300"
      >
        <div className="bg-brand-green-50 border-b border-brand-green-100 p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <SparklesIcon className="w-8 h-8 text-brand-green" />
            <div>
              <h2 id="market-intel-title" className="text-xl font-black text-card-title">Market Intelligence</h2>
              <p className="text-xs text-app-text-secondary uppercase font-bold tracking-widest">Powered by Gemini Search</p>
            </div>
          </div>
          <button
            onClick={closeIntel}
            aria-label="Close"
            className="p-2 text-brand-green hover:bg-brand-green-100 rounded-full transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          <form onSubmit={handleSearch} className="relative mb-6">
            <input 
              type="text" 
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Ask about crop prices, seasons, or trends..."
              aria-label="Ask about crop prices, seasons, or trends"
              className="w-full pl-12 pr-28 py-4 bg-input-bg text-input-text border-2 border-input-border rounded-2xl hover:border-input-border-hover focus:border-brand-green focus:ring-2 focus:ring-focus-ring outline-none font-bold transition-all"
              disabled={isSearching}
            />
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-app-text-secondary" />
            <button 
              type="submit"
              disabled={isSearching || !query.trim()}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-btn-primary text-btn-primary-text px-4 py-2 rounded-xl text-xs font-black uppercase hover:bg-btn-primary-hover disabled:opacity-50 transition-colors"
            >
              {isSearching ? 'Analysing...' : 'Search'}
            </button>
          </form>

          {result ? (
            <div className="space-y-6 animate-in fade-in duration-500 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
              <div className="bg-app-bg p-6 rounded-2xl border border-card-border text-app-text">
                <p className="leading-relaxed text-sm whitespace-pre-wrap">{result.answer}</p>
              </div>

              {result.sources.length > 0 && (
                <div>
                  <h3 className="text-[10px] font-black uppercase text-app-text-secondary tracking-widest mb-3 flex items-center gap-2">
                    <GlobeAltIcon className="w-3 h-3" />
                    Verified Sources
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {result.sources.map((source, i) => (
                      <a 
                        key={i}
                        href={source.uri}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-card-bg border border-card-border px-3 py-1.5 rounded-full text-[10px] font-bold text-brand-green hover:border-brand-green transition-all shadow-card flex items-center gap-1.5"
                      >
                        <span className="w-1 h-1 bg-brand-green rounded-full" />
                        {source.title}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-12 text-center">
              <div className="text-4xl mb-4 opacity-20" aria-hidden="true">📊</div>
              <p className="text-app-text-secondary font-bold italic text-sm">Ask anything like "Current plantain price in Paramaribo" or "When is the next harvest season for ginger?"</p>
            </div>
          )}
        </div>
        
        <div className="bg-brand-brown-50 p-4 border-t border-divider text-[9px] text-center font-black text-app-text-secondary uppercase tracking-tighter">
          Phase 11 Production Environment • Enterprise Grounding Enabled
        </div>
      </div>
    </div>
  );
};

export default MarketIntel;
